import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faUser,
    faBank,
    faCreditCard,
    faIdCard,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import {BankInfo, EventUserProfile} from '../../types';
import { useTranslation } from 'react-i18next';

interface UserInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    userProfile: EventUserProfile | null;
    participantName: string;
}

const UserInfoModal: React.FC<UserInfoModalProps> = ({
                                                         isOpen,
                                                         onClose,
                                                         userProfile,
                                                         participantName,
                                                     }) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    const renderBankInfo = (bankInfo: BankInfo) => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={faBank} className="mr-2 text-blue-600" />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            {t('components.userInfoModal.bankName')}
                        </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{bankInfo.bankName}</p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={faCreditCard} className="mr-2 text-green-600" />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            {t('components.userInfoModal.accountNumber')}
                        </span>
                    </div>
                    <p className="text-lg font-mono font-semibold text-gray-900 dark:text-white">{bankInfo.accountNumber}</p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={faIdCard} className="mr-2 text-purple-600" />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            {t('components.userInfoModal.accountHolder')}
                        </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{bankInfo.accountHolder}</p>
                </div>
            </div>
        </div>
    );

    const renderNoBankInfo = () => (
        <div className="text-center py-8">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl text-yellow-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('components.userInfoModal.noBankInfo')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
                {t('components.userInfoModal.noBankInfoMessage', { participantName })}
            </p>
        </div>
    );

    const renderAnonymousUser = () => (
        <div className="text-center py-8">
            <FontAwesomeIcon icon={faUser} className="text-6xl text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('components.userInfoModal.anonymousUser')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
                {t('components.userInfoModal.anonymousUserMessage')}
            </p>
        </div>
    );

    return createPortal(
        <div className="glass-modal-overlay">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">

                {/* Modal */}
                <div className="glass-modal inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full mr-3">
                                <FontAwesomeIcon icon={faUser} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {participantName}
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t('components.userInfoModal.bankingInformation')}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    {/* Content - Simplified logic */}
                    <div className="space-y-4">
                        {!userProfile ? (
                            renderAnonymousUser()
                        ) : !userProfile.bankInfo ? (
                            renderNoBankInfo()
                        ) : (
                            renderBankInfo(userProfile.bankInfo)
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UserInfoModal;